import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LocalizedText, PriceMode } from '@cr/shared';
import { api } from '@/lib/api';
import { dateRange, localized, money } from '@/lib/format';
import { useSession } from '@/lib/session';
import { haptic, setMainButton } from '@/lib/telegram';
import Icon from '@/components/icon';
import { Button, Card, EmptyState, Row, Screen, SectionTitle, Skeleton } from '@/components/ui';

interface CartItem {
  id: string;
  qty: number;
  startDate: string;
  endDate: string;
  days: number;
  rentTotal: number;
  accessoriesTotal: number;
  depositTotal: number;
  subtotal: number;
  available: boolean;
  issue: string | null;
  variant: {
    id: string;
    size: { value: string } | null;
    color: { name: LocalizedText; hex: string } | null;
  };
  product: {
    id: string;
    name: LocalizedText;
    pricePerDay: number;
    image: { thumbUrl: string } | null;
  };
  accessories: Array<{
    accessoryId: string;
    name: LocalizedText;
    price: number;
    priceMode: PriceMode;
  }>;
}

interface Cart {
  items: CartItem[];
  totals: { itemsTotal: number; accessoriesTotal: number; depositTotal: number; subtotal: number };
  canCheckout: boolean;
}

export default function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authorized, insideTelegram } = useSession();

  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: async () => (await api.get<Cart>('/cart')).data,
    enabled: authorized,
  });

  const remove = useMutation({
    mutationFn: (itemId: string) => api.delete(`/cart/items/${itemId}`),
    onSuccess: () => {
      haptic.tap();
      void queryClient.invalidateQueries({ queryKey: ['cart'] });
      void queryClient.invalidateQueries({ queryKey: ['cart-count'] });
    },
  });

  const checkout = () => {
    haptic.tap();
    navigate('/checkout');
  };

  useEffect(() => {
    if (!cart.data?.canCheckout) return setMainButton(null);

    return setMainButton({
      text: `${t('cart.checkout')} · ${money(cart.data.totals.subtotal + cart.data.totals.depositTotal)}`,
      onClick: checkout,
    });
  }, [cart.data?.canCheckout, cart.data?.totals.subtotal, t]);

  if (!authorized) {
    return (
      <Screen>
        <SectionTitle>{t('cart.title')}</SectionTitle>
        <EmptyState title={t('common.outsideTelegram')} />
      </Screen>
    );
  }

  if (cart.isLoading) {
    return (
      <Screen>
        <SectionTitle>{t('cart.title')}</SectionTitle>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </Screen>
    );
  }

  if (!cart.data || cart.data.items.length === 0) {
    return (
      <Screen>
        <SectionTitle>{t('cart.title')}</SectionTitle>
        <EmptyState
          title={t('cart.empty')}
          hint={t('cart.emptyHint')}
          action={
            <Button variant="secondary" onClick={() => navigate('/categories')}>
              {t('cart.goToCatalog')}
            </Button>
          }
        />
      </Screen>
    );
  }

  const { items, totals } = cart.data;

  return (
    <Screen>
      <SectionTitle>{t('cart.title')}</SectionTitle>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex gap-3 rounded-card bg-surface p-3 shadow-soft">
            <button onClick={() => navigate(`/product/${item.product.id}`)} className="shrink-0">
              {item.product.image ? (
                <img
                  src={item.product.image.thumbUrl}
                  alt=""
                  className="size-20 rounded-xl object-cover"
                />
              ) : (
                <span className="grid size-20 place-items-center rounded-xl bg-line text-muted/40">
                  <Icon name="shirt" size={32} stroke={1.2} />
                </span>
              )}
            </button>

            <div className="min-w-0 flex-1 space-y-1">
              <p className="line-clamp-2 text-sm font-medium">{localized(item.product.name)}</p>

              <p className="flex items-center gap-1.5 text-xs text-muted">
                {item.variant.size?.value}
                {item.variant.color && (
                  <>
                    <span
                      className="inline-block size-2.5 rounded-full border border-line"
                      style={{ background: item.variant.color.hex }}
                    />
                    {localized(item.variant.color.name)}
                  </>
                )}
              </p>

              <p className="text-xs text-muted">
                {dateRange(item.startDate, item.endDate)} ·{' '}
                {t('product.days', { count: item.days })}
              </p>

              {item.accessories.length > 0 && (
                <p className="text-xs text-muted">
                  + {item.accessories.map((a) => localized(a.name)).join(', ')}
                </p>
              )}

              {!item.available && (
                <p className="text-xs text-error">{item.issue ?? t('cart.unavailableItem')}</p>
              )}

              <div className="flex items-center justify-between pt-1">
                <span className="text-sm font-semibold">{money(item.subtotal)}</span>
                <button
                  onClick={() => remove.mutate(item.id)}
                  className="text-xs text-muted active:opacity-60"
                >
                  {t('cart.remove')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Card className="space-y-1">
        <Row label={t('cart.rent')} value={money(totals.itemsTotal)} />
        {totals.accessoriesTotal > 0 && (
          <Row label={t('cart.accessories')} value={money(totals.accessoriesTotal)} />
        )}
        {totals.depositTotal > 0 && (
          <Row
            label={t('cart.deposit')}
            hint={t('cart.depositHint')}
            value={money(totals.depositTotal)}
          />
        )}
        <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
          <span className="font-medium">{t('cart.total')}</span>
          <span className="text-lg font-semibold">
            {money(totals.subtotal + totals.depositTotal)}
          </span>
        </div>
      </Card>

      {!insideTelegram && (
        <Button className="w-full" disabled={!cart.data.canCheckout} onClick={checkout}>
          {t('cart.checkout')}
        </Button>
      )}
    </Screen>
  );
}
